
import { ArrowRight, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AdZone } from "@/types/adZones";
import { cn } from "@/lib/utils";
import { AdZoneFooterGuides } from "./AdZoneFooterGuides";
import { getActiveFooterCards } from "@/utils/adZoneFooterUtils";

interface AdZoneDisplayProps {
  zone: AdZone | null;
  variant: "banner" | "sidebar" | "footer";
  className?: string;
}

const hasContent = (zone: AdZone | null): zone is AdZone => {
  if (!zone || !zone.is_active) {
    return false;
  }

  if (zone.zone_key === "footer") {
    return getActiveFooterCards(zone).length > 0;
  }

  return !!(zone.title.trim() || zone.description.trim() || zone.image_url);
};

export const AdZoneDisplay = ({ zone, variant, className }: AdZoneDisplayProps) => {
  if (!hasContent(zone)) {
    return null;
  }

  const cta = zone.cta_label && zone.cta_url ? (
    <Button
      asChild
      size={variant === "sidebar" ? "sm" : variant === "banner" ? "sm" : "lg"}
      className={cn(
        "group shrink-0",
        variant === "banner" && "bg-white text-[#b31258] hover:bg-[#ffe7f2]",
        variant === "sidebar" && "w-full bg-[#EFB036] text-[#23486a] hover:bg-[#f5c04d] text-xs"
      )}
    >
      <a href={zone.cta_url} target="_blank" rel="noopener noreferrer">
        {zone.cta_label}
        {variant !== "sidebar" && (
          <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
        )}
        {variant === "sidebar" && <ExternalLink className="ml-1 h-3 w-3" />}
      </a>
    </Button>
  ) : null;

  if (variant === "banner") {
    return (
      <section className={cn("w-full bg-[#e71d75] text-white border-b border-white/10", className)}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-2.5 sm:py-3">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            {zone.image_url && (
              <img
                src={zone.image_url}
                alt={zone.title || "Publication"}
                className="h-12 w-12 sm:h-14 sm:w-14 rounded-lg object-cover shrink-0 border border-white/20"
              />
            )}
            <div className="flex-1 text-center sm:text-left min-w-0">
              {zone.title && (
                <p className="font-semibold text-sm sm:text-base truncate">{zone.title}</p>
              )}
              {zone.description && (
                <p className="text-xs sm:text-sm text-white/80 line-clamp-2">{zone.description}</p>
              )}
            </div>
            {cta}
          </div>
        </div>
      </section>
    );
  }

  if (variant === "sidebar") {
    return (
      <div className={cn("mx-3 mb-3 rounded-lg border border-white/15 bg-[#1a324d] overflow-hidden", className)}>
        {zone.image_url && (
          <img
            src={zone.image_url}
            alt={zone.title || "Publication"}
            className="w-full h-24 object-cover"
          />
        )}
        <div className="p-3 space-y-2">
          {zone.title && (
            <p className="font-semibold text-sm leading-tight">{zone.title}</p>
          )}
          {zone.description && (
            <p className="text-xs text-white/75 line-clamp-3">{zone.description}</p>
          )}
          {cta}
        </div>
      </div>
    );
  }

  if (variant === "footer") {
    return <AdZoneFooterGuides zone={zone} className={className} />;
  }

  return null;
};
